import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { ADMIN_COLORS } from '@/constants/admin';
import { useUpdateResidentInfoMutation } from '@/store/api';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { IUpdateResidentInfoRequest } from '@/types/admin/residentDetails';
import { composeValidators, requiredString, emailFormat, phoneFormat, validateData } from '@/utils/admin/validation';

interface ResidentEditFormProps {
  accountId?: number;
  residentDetails: {
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    email?: string;
  };
}

export const ResidentEditForm: React.FC<ResidentEditFormProps> = ({
  accountId,
  residentDetails,
}) => {
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const [updateResidentInfo, { isLoading }] = useUpdateResidentInfoMutation();

  // Form state
  const [formData, setFormData] = useState<IUpdateResidentInfoRequest>({
    leaseCustomerId: accountId,
    firstName: residentDetails.firstName || '',
    lastName: residentDetails.lastName || '',
    phonenumber: residentDetails.phoneNumber || '',
    email: residentDetails.email || '',
  });

  // Validation errors
  const [errors, setErrors] = useState<Partial<Record<keyof IUpdateResidentInfoRequest, string>>>({});
  
  // Sync form state when residentDetails prop changes
  useEffect(() => {
    setFormData({
      leaseCustomerId: accountId,
      firstName: residentDetails.firstName || '',
      lastName: residentDetails.lastName || '',
      phonenumber: residentDetails.phoneNumber || '',
      email: residentDetails.email || '',
    });
    // Clear errors when new data is loaded
    setErrors({});
  }, [residentDetails, accountId]);

  const validateForm = (): boolean => {
    const validator = composeValidators<IUpdateResidentInfoRequest>(
      requiredString('firstName', 'First name'),
      requiredString('lastName', 'Last name'),
      emailFormat('email'),
      phoneFormat('phonenumber'),
    );
    return validateData(formData, setErrors as any, validator);
  };

  const handleChange = (field: keyof IUpdateResidentInfoRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const result = await updateResidentInfo(formData).unwrap();
      if (result?.success) {
        showSuccess('User updated successfully');
      }
    } catch (error) {
      showApiError(error);
    }
  };

  const renderInput = (
    field: keyof IUpdateResidentInfoRequest,
    label: string,
    placeholder: string,
    keyboardType: 'default' | 'email-address' | 'phone-pad' = 'default'
  ) => (
    <View style={styles.inputContainer}>
      <Text style={styles.label}>{label}</Text>
      <TextInput
        style={[styles.input, errors[field] && styles.inputError]}
        value={formData[field]?.toString() || ''}
        onChangeText={(value) => handleChange(field, value)}
        placeholder={placeholder}
        placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
        keyboardType={keyboardType}
        autoCapitalize={field === 'email' ? 'none' : 'words'}
        autoCorrect={false}
      />
      {errors[field] && (
        <Text style={styles.errorText}>{errors[field]}</Text>
      )}
    </View>
  );

  return (
    <KeyboardAwareScrollView 
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      enableOnAndroid={true}
      enableAutomaticScroll={true}
      extraScrollHeight={Platform.OS === 'ios' ? 20 : 40}
      extraHeight={Platform.OS === 'ios' ? 20 : 40}
      enableResetScrollToCoords={false}
      keyboardOpeningTime={0}
    >
      <View style={styles.form}>
        <Text style={styles.title}>Edit Resident Information</Text>

        {renderInput('firstName', 'First Name', 'Enter first name')}
        {renderInput('lastName', 'Last Name', 'Enter last name')}
        {renderInput('phonenumber', 'Phone Number', 'Enter phone number', 'phone-pad')}
        {renderInput('email', 'Email', 'Enter email address', 'email-address')}

        <TouchableOpacity
          style={[styles.submitButton, isLoading && styles.submitButtonDisabled]}
          onPress={handleSubmit}
          disabled={isLoading}
          activeOpacity={0.7}
        >
          {isLoading ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <Text style={styles.submitButtonText}>Save Updates</Text>
          )}
        </TouchableOpacity>
      </View>
    </KeyboardAwareScrollView>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 30, // Extra space for keyboard
  },
  form: {
    backgroundColor: ADMIN_COLORS.SURFACE,
    margin: 16,
    padding: 20,
    borderRadius: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 24,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 8,
  },
  input: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  inputError: {
    borderColor: ADMIN_COLORS.ERROR,
  },
  errorText: {
    fontSize: 12,
    color: ADMIN_COLORS.ERROR,
    marginTop: 4,
  },
  submitButton: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 24,
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
